import { text } from '../../shared/text';
import { Play } from './icons';

/**
 * Her coach's own recording of a movement — offered, attributed, and honest
 * about the fact that there is nothing to play.
 *
 * **There is no video file in this product.** `exercise_library_item.video_path`
 * exists in the schema, **no migration creates a bucket for it**, and nothing
 * has ever uploaded one. `supabase/config.toml` has the bucket block commented
 * out; the only bucket named anywhere is the demo-GIF one, which ships
 * `enabled = false` and whose own comment says it "does not exist yet and must
 * not be created". `mvp-discovery` → `exercise-videos` (D17) specifies the whole
 * subsystem — private bucket, resumable upload, browser recording, poster
 * frames, signed URLs, an EU backup — and none of it is built.
 *
 * So what this does is show the offer — a play mark and her name on it, because
 * a client must always be able to tell her recording from the catalogue's stock
 * description — and then, when it is opened, say plainly that the file is not
 * there. A control that appeared to play something and silently did nothing
 * would be worse than the gap it hides.
 *
 * `<details>` rather than a modal: it opens without JavaScript, it closes the
 * same way, and it cannot strand a client in a player with no exit while they
 * are standing at a rack.
 *
 * It lives in its own file because two screens show it — the session, where the
 * exercise is being done, and the library, where her recordings are browsed —
 * and the sentence that admits the file is missing must be one sentence in one
 * place. Two copies is how one of them quietly stops being true.
 */
export function CoachVideo({ coach, className }: { coach: string; className?: string }) {
  return (
    <details className={className ?? 'mt-3'}>
      <summary className="text-primary flex min-h-11 cursor-pointer items-center gap-2 text-sm font-medium underline-offset-2 hover:underline">
        <span
          aria-hidden="true"
          className="bg-primary text-on-primary flex size-8 items-center justify-center rounded-full"
        >
          <Play size={16} />
        </span>
        {text('client.session.video', { coach })}
      </summary>
      <p className="text-ink-muted mt-2 text-xs">{text('client.session.videoMissing')}</p>
    </details>
  );
}
